import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import type { ApiKey, User } from "../types";

export function Settings() {
  useEffect(() => {
    document.title = "vibe-check ⏵Settings";
  }, []);
  const { user } = useAuth();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [fullUser, setFullUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [revokingKey, setRevokingKey] = useState<number | null>(null);
  const [deletingEvents, setDeletingEvents] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const data = await api.getMe();
      setFullUser(data.user);
      setApiKeys(data.api_keys);
    } catch (err) {
      console.error("Failed to load user data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    setGeneratingKey(true);
    setNewApiKey(null);
    try {
      const result = await api.createApiKey();
      setNewApiKey(result.api_key);
      // Reload to show the new key in the list
      await loadUserData();
    } catch (err) {
      console.error("Failed to generate API key:", err);
      alert("Failed to generate API key");
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleRevokeKey = async (keyId: number) => {
    if (
      !confirm(
        "Are you sure you want to revoke this API key? This cannot be undone.",
      )
    ) {
      return;
    }
    setRevokingKey(keyId);
    try {
      await api.revokeApiKey(keyId);
      await loadUserData();
    } catch (err) {
      console.error("Failed to revoke API key:", err);
      alert("Failed to revoke API key");
    } finally {
      setRevokingKey(null);
    }
  };

  const handleDeleteAllEvents = async () => {
    if (deleteConfirmText !== "DELETE") {
      return;
    }
    setDeletingEvents(true);
    try {
      const result = await api.deleteAllEvents();
      alert(`Successfully deleted ${result.deleted_count} events.`);
      setDeleteConfirmText("");
    } catch (err) {
      console.error("Failed to delete events:", err);
      alert("Failed to delete events");
    } finally {
      setDeletingEvents(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-3xl">
      <h1 className="text-2xl font-bold text-white">Settings</h1>

      {/* Profile Section */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Profile</h2>
        <div className="flex items-center space-x-4">
          {(fullUser?.avatar_url || user?.avatar_url) && (
            <img
              src={fullUser?.avatar_url || user?.avatar_url}
              alt="Avatar"
              className="w-16 h-16 rounded-full"
            />
          )}
          <div>
            <p className="text-white font-medium">
              {fullUser?.username || user?.username}
            </p>
            {fullUser?.github_username && (
              <a
                href={`https://github.com/${fullUser.github_username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-400 hover:text-purple-300 text-sm"
              >
                @{fullUser.github_username}
              </a>
            )}
            {fullUser?.created_at && (
              <p className="text-gray-500 text-sm">
                Member since{" "}
                {format(parseISO(fullUser.created_at), "MMMM yyyy")}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* API Keys Section */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">API Keys</h2>
          <button
            onClick={handleGenerateKey}
            disabled={generatingKey}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {generatingKey ? "Generating..." : "Generate New Key"}
          </button>
        </div>
        <p className="text-gray-400 text-sm mb-4">
          Use API keys to authenticate the vibe-check Python client. Keep your
          keys secure and never share them publicly.
        </p>

        {/* New Key Display */}
        {newApiKey && (
          <div className="mb-4 p-4 bg-green-900/20 border border-green-700 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-green-400 font-medium">
                New API Key Created
              </span>
              <button
                onClick={() => setNewApiKey(null)}
                className="text-gray-400 hover:text-white"
              >
                &times;
              </button>
            </div>
            <p className="text-yellow-400 text-xs mb-2">
              Copy this key now - you won't be able to see it again!
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-gray-900 p-2 rounded text-green-300 text-sm font-mono break-all">
                {newApiKey}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(newApiKey);
                  alert("Copied to clipboard!");
                }}
                className="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 text-sm"
              >
                Copy
              </button>
            </div>
          </div>
        )}

        {apiKeys.length > 0 ? (
          <div className="space-y-3">
            {apiKeys.map((key) => (
              <div
                key={key.id}
                className="flex items-center justify-between p-3 bg-gray-700/50 rounded"
              >
                <div>
                  <code className="text-purple-400">{key.api_key_preview}</code>
                  <p className="text-xs text-gray-500 mt-1">
                    Created {format(parseISO(key.created_at), "MMM d, yyyy")}
                    {key.last_used_at && (
                      <>
                        {" "}
                        · Last used{" "}
                        {format(parseISO(key.last_used_at), "MMM d, yyyy")}
                      </>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      key.is_active
                        ? "bg-green-900/30 text-green-400"
                        : "bg-red-900/30 text-red-400"
                    }`}
                  >
                    {key.is_active ? "Active" : "Revoked"}
                  </span>
                  {key.is_active && (
                    <button
                      onClick={() => handleRevokeKey(key.id)}
                      disabled={revokingKey === key.id}
                      className="px-2 py-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded disabled:opacity-50"
                    >
                      {revokingKey === key.id ? "Revoking..." : "Revoke"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 bg-gray-700/30 rounded">
            <p className="text-gray-400">No API keys</p>
            <p className="text-sm text-gray-500 mt-1">
              Click "Generate New Key" above or use the CLI to authenticate
            </p>
          </div>
        )}

        <div className="mt-4 p-4 bg-gray-700/30 rounded">
          <h3 className="text-sm font-medium text-white mb-2">
            CLI Authentication
          </h3>
          <p className="text-sm text-gray-400 mb-3">
            The easiest way to authenticate the vibe-check CLI:
          </p>
          <code className="block bg-gray-900 p-3 rounded text-sm text-gray-300">
            vibe-check auth login
          </code>
          <p className="text-xs text-gray-500 mt-2">
            This will open a browser window to authorize your CLI device.
          </p>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-gray-800 rounded-lg p-6 border border-red-900/50">
        <h2 className="text-lg font-semibold text-red-400 mb-4">Danger Zone</h2>

        <div className="p-4 bg-red-900/20 border border-red-800 rounded-lg">
          <h3 className="text-white font-medium mb-2">Delete All Events</h3>
          <p className="text-gray-400 text-sm mb-4">
            Permanently delete all your conversation events from the server.
            This will remove all your tracked conversations, messages, and
            associated data.
          </p>
          <div className="p-3 bg-red-950/50 border border-red-800 rounded mb-4">
            <p className="text-red-400 text-sm font-medium">
              Warning: This action cannot be undone. All your data will be
              permanently deleted.
            </p>
          </div>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-sm text-gray-400 mb-1">
                Type DELETE to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white placeholder-gray-600 focus:outline-none focus:border-red-500"
              />
            </div>
            <button
              onClick={handleDeleteAllEvents}
              disabled={deleteConfirmText !== "DELETE" || deletingEvents}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deletingEvents ? "Deleting..." : "Delete All Events"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
